/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import doFetchApi from '@canvas/do-fetch-api-effect'

declare global {
  interface Window {
    // undefined until _head.html.erb publishes it from PRE_FUNCTIONAL_COOKIE_CONSENT
    CANVAS_FUNCTIONAL_COOKIE_CONSENT_STATE: boolean | null | undefined
    CANVAS_COOKIE_CONSENT_STATE: boolean | null
  }
}

interface CookieType {
  name: string
  value: string
  domain?: string
  path?: string
  expires?: Date
  secure?: boolean
  sameSite?: 'Strict' | 'Lax' | 'None'
}

export const oneTrustFunctionalCookieClass: string = 'C0003'
const serverSetFunctionalCookies: readonly string[] = [
  'canvas_otp_remember_me',
  'canvas_functional_cookie_test',
]
const clientSetFunctionalCookies: readonly string[] = [
  'unsupported_browser_dismissed',
  'ui-tabs-*',
  'kaltura_analytic_tracker',
  'k5_observed_user_for_*',
  'canvas_functional_cookie_test_clientside',
]

const waitingCookieSets: CookieType[] = []

const functionalConsentGranted = (): boolean => {
  const state = window.CANVAS_FUNCTIONAL_COOKIE_CONSENT_STATE
  return state !== false && state !== null
}

const writeCookie = (cookie: CookieType): void => {
  const attributes = [
    `${encodeURIComponent(cookie.name)}=${encodeURIComponent(cookie.value)}`,
    `path=${cookie.path || '/'}`,
  ]
  if (cookie.domain) attributes.push(`domain=${cookie.domain}`)
  if (cookie.expires) attributes.push(`expires=${cookie.expires.toUTCString()}`)
  if (cookie.secure) attributes.push('secure')
  if (cookie.sameSite) attributes.push(`SameSite=${cookie.sameSite}`)
  document.cookie = attributes.join('; ')
}

const clearableDomains = (): string[] => {
  const host = window.location.hostname
  const labels = host.split('.')
  const domains = [host]
  for (let i = 1; i <= labels.length - 2; i++) {
    domains.push(`.${labels.slice(i).join('.')}`)
  }
  return domains
}

const expireCookie = (name: string): void => {
  const expired = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
  document.cookie = expired
  for (const domain of clearableDomains()) {
    document.cookie = `${expired}; domain=${domain}`
  }
}

export const setFunctionalCookie = (
  name: string,
  value: string,
  domain: string = '',
  path: string = '/',
  expires?: Date,
  secure?: boolean,
  sameSite?: 'Strict' | 'Lax' | 'None',
): void => {
  const cookie: CookieType = {name, value, domain, path, expires, secure, sameSite}
  if (functionalConsentGranted()) {
    writeCookie(cookie)
    console.debug(`Functional cookie set: ${cookie.name}=${cookie.value}`)
  } else {
    waitingCookieSets.push(cookie)
    console.debug(`Functional cookie queued: ${cookie.name}=${cookie.value}`)
  }
}

export const clearFunctionalCookies = (): void => {
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const cookieName = cookie.split('=')[0].trim()
    if (
      serverSetFunctionalCookies.includes(cookieName) ||
      clientSetFunctionalCookies.some(pattern => {
        const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\\\*/g, '.*')
        return new RegExp(`^${escaped}$`).test(cookieName)
      })
    ) {
      expireCookie(cookieName)
      console.debug(`Functional cookie cleared: ${cookieName}`)
    }
  }
}

export const processWaitingCookieSets = (): void => {
  if (functionalConsentGranted()) {
    waitingCookieSets.forEach(cookie => {
      writeCookie(cookie)
      console.debug(`Functional cookie set from queue: ${cookie.name}=${cookie.value}`)
    })
    waitingCookieSets.length = 0
  }
}

export const FUNCTIONAL_COOKIES_PATH = '/users/self/functional_cookies'
const TEST_FUNCTIONAL_COOKIE_PATH = '/api/v1/users/self/settings?include=functional_cookie_test'
const SERVER_CLEARED_KEY = 'canvas_functional_cookies_cleared'

const sessionFlag = {
  get(): boolean {
    try {
      return window.sessionStorage.getItem(SERVER_CLEARED_KEY) === 'true'
    } catch {
      return false
    }
  },
  set(value: boolean): void {
    try {
      if (value) {
        window.sessionStorage.setItem(SERVER_CLEARED_KEY, 'true')
      } else {
        window.sessionStorage.removeItem(SERVER_CLEARED_KEY)
      }
    } catch {
      // no-op
    }
  },
}

export const requestServerFunctionalCookieClear = (): Promise<void> =>
  doFetchApi({path: FUNCTIONAL_COOKIES_PATH, method: 'DELETE'})
    .then(() => {
      sessionFlag.set(true)
      console.debug('Server-set functional cookies cleared.')
    })
    .catch(() => {
      console.debug('Failed to clear server-set functional cookies; will retry on next load.')
    })

const requestServerFunctionalCookieSet = (): Promise<void> =>
  doFetchApi({path: TEST_FUNCTIONAL_COOKIE_PATH, method: 'GET'})
    .then(() => {
      sessionFlag.set(true)
      console.debug('Server-set functional cookies test set.')
    })
    .catch(() => {
      console.error('Could not set test functional cookie on server side')
    })
export const reconcileFunctionalCookies = (): void => {
  if (window.CANVAS_FUNCTIONAL_COOKIE_CONSENT_STATE === false) {
    console.debug('Functional cookie consent was already revoked before boot.')
    clearFunctionalCookies()
    if (!sessionFlag.get()) {
      requestServerFunctionalCookieClear()
    }
  }
}

export const handleOneTrustGroupsUpdated = (detail: string[] | string): void => {
  if (detail.includes(oneTrustFunctionalCookieClass)) {
    window.CANVAS_FUNCTIONAL_COOKIE_CONSENT_STATE = true
    console.debug('Functional cookie consent granted.')
    sessionFlag.set(false)
    processWaitingCookieSets()
  } else {
    window.CANVAS_FUNCTIONAL_COOKIE_CONSENT_STATE = false
    console.debug('Functional cookie consent revoked.')
    clearFunctionalCookies()
    requestServerFunctionalCookieClear()
  }
}

export const handleFunctionalCookieTest = (detail: string[] | string): void => {
  requestServerFunctionalCookieSet()
  setFunctionalCookie('canvas_functional_cookie_test_clientside', 'agrajag', '', '/')
  console.debug('Test functional cookies set.')
}
